import React, { useMemo, useEffect } from 'react'
import { Col, Row } from 'react-bootstrap'
import {
  TextAlign,
  RowSelectEventArgs,
  GridComponent,
} from '@syncfusion/ej2-react-grids'
import Button from '@components/common/Button/Button.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import { InsurancePlan as InsurancePlanType } from '@dataTypes/insurance.types'

interface InsurancePlanProps {
  insurancePlans: InsurancePlanType[]
  onSelectInsurancePlan: (args: RowSelectEventArgs) => void
  gridRef: React.RefObject<GridComponent>
  handleSetPrimaryInsurancePlanClick: (insurance_plan_id: string) => void
  handleDeleteInsurancePlanClick: (insurance_plan_id: string) => void
  outlined?: boolean
}

const InsurancePlan: React.FC<InsurancePlanProps> = ({
  insurancePlans,
  onSelectInsurancePlan,
  gridRef,
  handleSetPrimaryInsurancePlanClick,
  handleDeleteInsurancePlanClick,
  outlined = false,
}) => {
  const carrierInsuranceTemplate = ({
    carrier_name,
    is_primary,
  }: {
    carrier_name: string
    is_primary: boolean
  }): JSX.Element => (
    <div className="template-cell">
      {carrier_name}
      {is_primary && carrier_name !== '-' && (
        <span className="template-cell carrier-primary insurance-badge-status">
          Primary
        </span>
      )}
    </div>
  )

  const statusInsuranceTemplate = ({
    status,
  }: {
    status: string
  }): JSX.Element => {
    const isActive = status === 'Active'
    const statusClass = isActive ? 'e-activecolor' : 'e-inactivecolor'

    return (
      <div
        className={`template-cell ${status !== '-' && `statustemp ${statusClass}`} insurance-badge-status`}
      >
        <span
          className={`template-cell ${status !== '-' && `statustxt ${statusClass}`}`}
        >
          {status}
        </span>
      </div>
    )
  }

  const actionsInsuranceTemplate = ({
    insurance_plan_id,
    status,
    is_primary,
  }: {
    insurance_plan_id: string
    status: string
    is_primary: boolean
  }): JSX.Element => {
    return status === '-' ? (
      <></>
    ) : (
      <div className="template-cell actions-container">
        <Button
          className={`template-cell set-primary-button ${!is_primary ? 'inactive' : ''}`}
          onClick={() => handleSetPrimaryInsurancePlanClick(insurance_plan_id)}
          title="Set this plan as primary"
        >
          <span
            className={`template-cell icon ${is_primary ? 'mgc_safety_certificate_fill' : 'mgc_shield_shape_line inactive'}`}
          ></span>
        </Button>
        <Button
          className="template-cell remove-plan-button"
          onClick={() => handleDeleteInsurancePlanClick(insurance_plan_id)}
          title="Delete this plan"
        >
          <span className="template-cell icon mgc_delete_2_fill"></span>
        </Button>
      </div>
    )
  }

  const queryCellInfo = (args: any): void => {
    args.cell.setAttribute('title', 'Select this record')
  }

  const columnsInsurancePlan = useMemo(() => {
    return [
      {
        field: 'number',
        headerText: '#',
        width: '40',
        textAlign: 'Center' as TextAlign,
      },
      {
        field: 'carrier_name',
        headerText: 'Carrier',
        width: '180px',
        textAlign: 'Left' as TextAlign,
        template: carrierInsuranceTemplate,
      },
      {
        field: 'plan_name',
        headerText: 'Plan',
        width: '240px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'plan_type',
        headerText: 'Plan type',
        width: '90px',
        textAlign: 'Left' as TextAlign,
        template: ({ plan_type }: { plan_type: string }): JSX.Element => (
          <div className="template-cell">{plan_type || '-'}</div>
        ),
      },
      {
        field: 'subscriber_fullname',
        headerText: 'Subscriber',
        width: '90px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'subscriber_member_id',
        headerText: 'Subscriber ID',
        width: '90px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'subscriber_dob',
        headerText: 'Subscriber DOB',
        width: '90px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'status',
        headerText: 'Status',
        width: '70px',
        textAlign: 'Left' as TextAlign,
        template: statusInsuranceTemplate,
      },
      {
        field: 'actions',
        headerText: 'Actions',
        width: '80px',
        textAlign: 'Center' as TextAlign,
        template: actionsInsuranceTemplate,
      },
    ].map((column) => ({
      ...column,
    }))
  }, [])

  useEffect(() => {
    const gridElement = document.querySelector('.e-grid') as HTMLElement

    const applyHoverStyles = (rowCells: NodeListOf<HTMLElement>): void => {
      rowCells.forEach((cell) => {
        if (!cell.classList.contains('e-active')) {
          ;(cell as HTMLElement).style.setProperty(
            'background-color',
            '#d8dee4',
            'important',
          )
          ;(cell as HTMLElement).style.setProperty(
            'cursor',
            'pointer',
            'important',
          )
        }
      })
    }

    const removeHoverStyles = (rowCells: NodeListOf<HTMLElement>): void => {
      rowCells.forEach((cell) => {
        ;(cell as HTMLElement).style.removeProperty('background-color')
        ;(cell as HTMLElement).style.removeProperty('cursor')
      })
    }

    const handleRemoveHoverStyles = (e: MouseEvent): void => {
      const target = e.target as HTMLElement
      if (
        target &&
        (target.classList.contains('e-rowcell') ||
          target.classList.contains('template-cell'))
      ) {
        const row = target.closest('.e-row') as HTMLElement
        if (row) {
          const rowCells = row.querySelectorAll('.e-rowcell')
          removeHoverStyles(rowCells as NodeListOf<HTMLElement>)
        }
      }
    }

    const handleMouseover = (e: MouseEvent): void => {
      const target = e.target as HTMLElement
      if (
        target &&
        (target.classList.contains('e-rowcell') ||
          target.classList.contains('template-cell'))
      ) {
        const row = target.closest('.e-row') as HTMLElement
        if (row) {
          const rowCells = row.querySelectorAll('.e-rowcell')
          applyHoverStyles(rowCells as NodeListOf<HTMLElement>)
        }
      }
    }

    if (gridElement) {
      gridElement.addEventListener('mouseover', handleMouseover)
      gridElement.addEventListener('mouseout', handleRemoveHoverStyles)
      gridElement.addEventListener('click', handleRemoveHoverStyles)
    }

    return () => {
      if (gridElement) {
        gridElement.removeEventListener('mouseover', handleMouseover)
        gridElement.removeEventListener('mouseout', handleRemoveHoverStyles)
        gridElement.removeEventListener('click', handleRemoveHoverStyles)
      }
    }
  }, [])

  return (
    <div className="section-insurance-plan">
      {insurancePlans.length === 0 ? (
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle="No insurance plans found"
          description="You don't have any insurance plans on record yet."
          outlined={outlined}
        />
      ) : (
        <>
          <Row>
            <Col>
              <div>
                <p className="section-title">Insurance plan</p>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="grid-container">
                <DataGridComponent
                  ref={gridRef}
                  data={insurancePlans}
                  columns={columnsInsurancePlan}
                  rowHeight={36}
                  allowPaging={false}
                  allowSorting={false}
                  rowSelected={onSelectInsurancePlan}
                  selectionSettings={{
                    type: 'Single',
                    mode: 'Row',
                    enableToggle: false,
                  }}
                  queryCellInfo={queryCellInfo}
                />
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}

export default InsurancePlan
